import { gofor } from '@fiverr-private/futile/lib/gofor';
import mapBodyToEntity from '../../utils/mapBodyToEntity';
import { toSnakeCase } from '../../utils/changeCase';
import * as Error from '../../utils/error';
import * as CommentsErrors from '../../errors/comments';
import { DEFAULT_ANNOTATION_COLOR, getRandomColor } from '../../../MediaViewer/Viewers/PDF/annotation-color-pallete';

/**
 * Create comments.
 * @param {object} payload
 * @param {array<Comment>} payload.comments[] - The comments to create.
 * @param {string} payload.comments[].id - comment id.
 * @param {string} payload.comments[].collaboratorId - id of collaborator who wrote the comment.
 * @param {string} payload.comments[].parentId - for replies, what comment they belong to.
 * @param {string} payload.comments[].groupId - id of the group the collaborator belongs to.
 * @param {string} payload.comments[].fileId - file id.
 * @param {string} payload.comments[].body - the comment content.
 * @param {number} payload.comments[].timecode - timecode in video/audio.
 * @param {array} payload.comments[].annotations[] - comments annotations.
 * @param {number} payload.comments[].annotations[].xPercentage - number between 0 and 100 ex: 12.23
 * @param {number} payload.comments[].annotations[].yPercentage - number between 0 and 100 ex: 42.23
 * @param {string} payload.comments[].annotations[].type - annotation type ex: dot
 * @param {string} payload.contextType
 * @param {(string|number)} payload.contextId
 * @param {string} [payload.reviewType] - type of the comments bulk to create
 * @returns {Promise}
 * @resolves {array} comments
 * @resolves {string} comments[].id - The comment id.
 * @resolves {string} comments[].collaboratorId - The comment's collaborator's id.
 * @resolves {string} comments[].parentId - for replies, what comment they belong to.
 * @resolves {string} comments[].groupId - id of the group the collaborator belongs to.
 * @resolves {string} comments[].fileId - file id.
 * @resolves {string} comments[].reviewId - the review the comment's belong to.
 * @resolves {array} comments[].annotations - The comment's annotations.
 * @resolves {number} comments[].annotations[].xPercentage - number between 0 and 100 ex: 12.23
 * @resolves {number} comments[].annotations[].yPercentage - number between 0 and 100 ex: 42.23
 * @resolves {string} comments[].annotations[].type - annotation type ex: dot
 * @resolves {string} comments[].body - the comment content.
 * @resolves {number} comments[].timecode - The comment's timecode in video/audio.
 * @resolves {number} comments[].createdAt - when the comment was created.
 * @rejects {Error}
 */
export const createComments = (payload) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                CommentsErrors.COMMENTS_APIS_CREATE_COMMENTS_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        payload.comments.map((comment) => {
            if (comment.annotations && comment.annotations.length && comment.annotations[0].target){
                if (comment.annotations[0].target.selector.strokeColor.toUpperCase() === DEFAULT_ANNOTATION_COLOR) {
                    comment.annotations[0].target.selector.strokeColor = getRandomColor();
                }
            }
            return comment;
        });
        const url = '/collaboration/reviews';
        const body = JSON.stringify(toSnakeCase(payload));
        const method = 'POST';

        return gofor(url, { method, body });
    }).then((response) => {
        if (response.status !== 201) {
            throw Error.create(
                `Http status received: ${response.status} expected: 201`,
                CommentsErrors.COMMENTS_APIS_CREATE_COMMENTS_BAD_SERVER_STATUS,
                payload
            );
        }

        return response.json();
    }).then((body) =>
        Object.values(mapBodyToEntity(body, 'comments'))
    ).catch((error) => {
        if (!error.code) {
            error.code = CommentsErrors.COMMENTS_APIS_CREATE_COMMENTS_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });
